import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { getCalendlyLink } from "../API/calendlyAPI";
import {
  confirmPayment,
  getPaymentBySessionId,
  getPaymentTransactionDisplayId,
} from "../API/paymentAPI";
import { getApplication } from "../API/applicationAPI";
import type { Application } from "../types/application";
import check from "../assets/check.png";

const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = searchParams.get("session_id");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [paymentDetails, setPaymentDetails] = useState<any>(null);
  const [application, setApplication] = useState<Application | null>(null);

  // Safely format amounts whether they arrive as number or string
  const formatAmount = (value: unknown): string => {
    const num =
      typeof value === "number" ? value : parseFloat(String(value ?? ""));
    return Number.isFinite(num) ? num.toFixed(2) : "0.00";
  };

  // Normalize to number (or NaN)
  const toNumber = (value: unknown): number =>
    typeof value === "number" ? value : parseFloat(String(value ?? ""));

  useEffect(() => {
    if (sessionId) {
      handlePaymentConfirmation(sessionId);
    } else {
      setError("No session ID found");
      setLoading(false);
    }
  }, [sessionId]);

  const handlePaymentConfirmation = async (sessionId: string) => {
    try {
      setLoading(true);

      // Retry logic: Try multiple times with delays
      // This helps when webhooks are slow or payment is still processing
      const maxRetries = 5;
      const retryDelay = 2000; // 2 seconds

      for (let attempt = 1; attempt <= maxRetries; attempt++) {
        try {
          console.log(`Attempt ${attempt}/${maxRetries}: Fetching payment...`);
          const payment = await getPaymentBySessionId(sessionId);

          // Check if payment is completed
          if (
            payment.status === "completed" ||
            payment.status === "succeeded"
          ) {
            console.log("Payment confirmed successfully:", payment);
            setPaymentDetails(payment);

            // Fetch application for breakdown display
            try {
              const app = await getApplication(payment.applicationId);
              setApplication(app);
            } catch (e) {
              console.log("Could not load application for breakdown:", e);
            }
            setLoading(false);
            return;
          }

          // If payment exists but not completed, wait and retry
          console.log(`Payment status: ${payment.status}, retrying...`);
          if (attempt < maxRetries) {
            await new Promise((resolve) => setTimeout(resolve, retryDelay));
          }
        } catch (getError: any) {
          console.log(
            `Attempt ${attempt} failed:`,
            getError?.response?.data?.message || getError?.message
          );

          // If payment not found and it's not the last attempt, wait and retry
          if (attempt < maxRetries) {
            await new Promise((resolve) => setTimeout(resolve, retryDelay));
          }
        }
      }

      // If all retries failed to get payment, try manual confirmation
      console.log(
        "All fetch attempts failed, attempting manual confirmation..."
      );
      const payment = await confirmPayment(sessionId);
      console.log("Manual confirmation successful:", payment);
      setPaymentDetails(payment);
      try {
        const app = await getApplication(payment.applicationId);
        setApplication(app);
      } catch (e) {
        console.log("Could not load application for breakdown:", e);
      }

      setLoading(false);
    } catch (error: any) {
      console.error("Error confirming payment:", error);
      setError(
        error?.response?.data?.message ||
          error?.message ||
          "Failed to confirm payment. Your payment was processed by Stripe, but we couldn't link it to your account. Please contact support with your session ID."
      );
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#f8f9fb] flex flex-col items-center justify-center px-4 pt-[200px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-[#1B365D] mx-auto mb-4"></div>
          <p className="text-[#1B365D] text-lg">Confirming your payment...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#f8f9fb] flex flex-col items-center justify-center px-4 pt-[200px]">
        <div className="max-w-2xl mx-auto w-full mb-6"></div>
        <div className="w-full max-w-2xl bg-white rounded-xl shadow-md p-5 md:p-8 mt-4 text-center">
          <div className="text-red-500 text-6xl mb-4">⚠️</div>
          <h2 className="text-[28px] text-[#1e2b5e] mb-4 font-georgia">
            Payment Confirmation Error
          </h2>
          <p className="text-[#1B365D] mb-6">{error}</p>

          <div className="bg-[#fff8f0] border-l-4 border-[#F4A261] p-4 mb-6 text-left">
            <p className="text-[#1B365D] text-sm">
              <span className="font-semibold">What this means:</span> Your
              payment may have been processed by Stripe, but we encountered an
              issue linking it to your application. Please contact support with
              your session ID:
              <code className="block mt-2 text-xs bg-gray-100 p-2 rounded">
                {sessionId}
              </code>
            </p>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => navigate("/dashboard")}
              className="bg-[#F4A261] text-[#1B365D] border-2 border-[#F4A261] font-semibold px-8 py-3 rounded-[50px] text-xl hover:bg-[#F29449]"
            >
              Go to Dashboard
            </button>
            <button
              onClick={() => {
                window.location.href = getCalendlyLink();
              }}
              className="bg-white text-[#1B365D] border-2 border-[#1B365D] font-semibold px-8 py-3 rounded-[50px] text-xl hover:bg-[#1B365D] hover:text-white"
            >
              Contact Support
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f8f9fb] flex flex-col items-center justify-center px-4 pt-[200px]">
      <div className="max-w-2xl mx-auto w-full mb-6"></div>
      <div className="w-full max-w-2xl bg-white rounded-xl shadow-md p-5 md:p-8 mt-4 text-center">
        <img
          src={check}
          alt="Success"
          className="w-[80px] h-[80px] mx-auto mb-6 rounded-full"
        />
        <h2 className="text-[28px] text-[#1e2b5e] mb-4 font-georgia">
          Payment Successful!
        </h2>
        <p className="text-[#1B365D] mb-6">
          Thank you for your payment. Your transaction has been completed
          successfully.
        </p>

        {paymentDetails && (
          <div className="bg-[#f1f2f6] p-6 rounded-lg mb-6 text-left">
            <h3 className="font-bold text-[#1B365D] mb-4">Payment Details</h3>
            <div className="space-y-2">
              <p className="text-[#1B365D]">
                <span className="font-semibold">Amount:</span>{" "}
                {paymentDetails.currency?.toUpperCase()} $
                {typeof paymentDetails.amount === "number"
                  ? paymentDetails.amount.toFixed(2)
                  : parseFloat(paymentDetails.amount || "0").toFixed(2)}
              </p>
              <p className="text-[#1B365D]">
                <span className="font-semibold">Status:</span>{" "}
                <span className="capitalize">{paymentDetails.status}</span>
              </p>
              <p className="text-[#1B365D] break-words">
                <span className="font-semibold">Transaction ID:</span>{" "}
                <span className="break-all font-mono text-sm">
                  {getPaymentTransactionDisplayId(paymentDetails)}
                </span>
              </p>
            </div>
          </div>
        )}

        {application?.uscisFee && application.uscisFee.status === "ready" && (
          <div className="bg-white border border-gray-200 p-6 rounded-lg mb-6 text-left">
            <h3 className="font-bold text-[#1B365D] mb-4">Payment Breakdown</h3>
            <div className="space-y-2">
              {(() => {
                const totalPaid = toNumber(paymentDetails?.amount);
                const uscisBreakdownTotal =
                  application?.uscisFee?.breakdown?.total;
                const uscisAmountNum = toNumber(
                  uscisBreakdownTotal ?? application.uscisFee.amount
                );
                const packageFeeNum =
                  Number.isFinite(totalPaid) && Number.isFinite(uscisAmountNum)
                    ? Math.max(totalPaid - uscisAmountNum, 0)
                    : totalPaid;
                return (
                  <>
                    <p className="text-[#1B365D]">
                      <span className="font-semibold">Package Fee:</span>{" "}
                      {paymentDetails?.currency?.toUpperCase()} $
                      {formatAmount(packageFeeNum)}
                    </p>
                    <p className="text-[#1B365D]">
                      <span className="font-semibold">USCIS Filing Fee:</span>{" "}
                      {application.uscisFee.currency?.toUpperCase()} $
                      {formatAmount(uscisAmountNum)}
                    </p>
                    <p className="text-[#1B365D]">
                      <span className="font-semibold">Total Paid:</span>{" "}
                      {paymentDetails?.currency?.toUpperCase()} $
                      {formatAmount(totalPaid)}
                    </p>
                  </>
                );
              })()}
              {application.uscisFee.breakdown && (
                <div className="text-[#1B365D] text-sm ml-2">
                  <p>
                    Base: {application.uscisFee.currency?.toUpperCase()} $
                    {formatAmount(application.uscisFee.breakdown.base)}
                  </p>
                  <p>
                    Biometrics: {application.uscisFee.currency?.toUpperCase()} $
                    {formatAmount(application.uscisFee.breakdown.biometrics)}
                  </p>
                  {Array.isArray(application.uscisFee.breakdown.notes) &&
                    application.uscisFee.breakdown.notes.length > 0 && (
                      <ul className="list-disc ml-5 mt-2">
                        {application.uscisFee.breakdown.notes.map((n, i) => (
                          <li key={i}>{n}</li>
                        ))}
                      </ul>
                    )}
                </div>
              )}
            </div>
          </div>
        )}

        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <button
            onClick={() => navigate("/dashboard")}
            className="bg-[#F4A261] text-[#1B365D] border-2 border-[#F4A261] font-semibold px-8 py-3 rounded-[50px] text-xl hover:bg-[#F29449]"
          >
            Go to Dashboard
          </button>
          <button
            onClick={() => navigate("/payment-history")}
            className="bg-white text-[#1B365D] border-2 border-[#1B365D] font-semibold px-8 py-3 rounded-[50px] text-xl hover:bg-[#1B365D] hover:text-white"
          >
            View Payment History
          </button>
        </div>

        <p className="text-sm text-gray-600 mt-6">
          A confirmation email has been sent to your registered email address.
        </p>
      </div>
    </div>
  );
};

export default PaymentSuccess;
